import React, { useState, useEffect } from 'react';
import { Table, Form, Button, Modal, Card } from "react-bootstrap";
import { Link, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faPlus, faEye, faPen, faToggleOn, faToggleOff, 
    faFilter, faSync, faUserShield 
} from '@fortawesome/free-solid-svg-icons';
import useFetch from "../../../hooks/useFetch";
import PaginatedItems from '../../../components/administracao/Pagination';

function GerenciarUsuarios() {
    // https://create-react-app.dev/docs/adding-custom-environment-variables/
    const { params } = useParams(); // pega o que vem depois da barra na url
    const { get, put } = useFetch();

    const defaultState = {
        usuarios: [],
        count: 0,
        page: 1,
        filtroAtivo: false,
        ordem: "",
        filtros: {
            nome_pessoa: "",
            dta_cadastro: "",
            status_usuario: "",
            funcao: ""
        },
        mostrarModalInativacao: false,
        registroAExcluir: {
            nome_pessoa: "",
            status_usuario: ""
        },
        loading: true
    }

    // Roda quando a página é carregada https://reactjs.org/docs/hooks-reference.html#lazy-initial-state
    const [state, setState] = useState(() => {
        return defaultState;
    });

    useEffect(() => {
        async function initialize() {
            let [usuarios, count] = await buscarUsuarios({});
            setState({ ...state, usuarios: usuarios, count: count, loading: false });
        }
        initialize();
    }, [params]);

    // função de paginação
    async function handlePageChange(pageNumber) {
        window.scrollTo(0, 0);
        let filtroPaginacao = { page: pageNumber };
        if (state.filtroAtivo) {
            filtroPaginacao = { ...filtroPaginacao, page: pageNumber };
        }
        let [usuarios, count] = await buscarUsuarios(filtroPaginacao);
        setState({ ...state, page: pageNumber, usuarios: usuarios, count: count, loading: false });
    }

    // Busca os dados do banco
    async function buscarUsuarios(filtros) {
        if (!state.loading) setState({ ...state, loading: true });
        // filtros = { nome_pessoa: "nome_pessoa", dta_cadastro: "dta_cadastro", funcao: "funcao" }
        let query = "?";
        if (filtros && Object.keys(filtros).length > 0) {
            for (const key in filtros) {
                if (filtros[key] !== "") {
                    query = query.concat(key, "=", filtros[key], "&");
                }
            }
        }

        try {
            let response = await get('/usuario' + query);
            if (response.ok) {
                let dados = response.result;
                return [dados.data, dados.count];
            }
        } catch (error) {
            console.error('Erro no buscarUsuarios():', error);
        }
    }

    // Função acionada quando o usuário clica em buscar (ou aperta Enter).
    // Aceita filtros/ordem explícitos opcionais — sem isso, quem chamava logo
    // depois de um setState (ex.: trocar a ordenação) acabava lendo o
    // state.filtros/state.ordem ainda com o valor antigo, já que o setState
    // não atualiza o state sincronamente.
    async function filtrarUsuarios(filtros = state.filtros, ordem = state.ordem) {
        // Busca os dados do banco com os filtros
        let [usuariostemp, count] = await buscarUsuarios(filtros);
        let usuarios = ordenar(usuariostemp, ordem);
        // Atualiza o estado com os dados buscados
        setState((prev) => ({ ...prev, usuarios: usuarios, count: count, filtroAtivo: true, loading: false }));
    }

    async function limparFiltros() {
        // Busca os dados do banco sem filtros
        let [usuarios, count] = await buscarUsuarios({});
        // Atualiza o estado com os dados buscados e reseta os valores dos campos de filtro
        setState({ ...state, filtros: defaultState.filtros, filtroAtivo: false, usuarios: usuarios, count: count, loading: false });
    }

    function handleChangeFiltros(event) {
        let target = event.target;
        let value = target.value;
        let name = target.name;

        if (target.type === 'select-one') {
            value = target.options[target.selectedIndex].value;
        }

        setState({
            ...state,
            filtros: {
                ...state.filtros,
                [name]: value
            }
        });
    }

    async function inativarUsuario() {
        let usuario = state.registroAExcluir;
        try {
            let rota = usuario.status_usuario === "Ativo" ? "/inativarusuario/" : "/ativarusuario/";
            let response = await put(rota + usuario.id_usuario);
            if (response.status === 201) {
                let [usuarios, count] = await buscarUsuarios({});
                setState({ ...state, usuarios: usuarios, count: count, mostrarModalInativacao: false, registroAExcluir: {}, loading: false });
            } else {
                console.error('Erro no inativarUsuario()');
            }

        } catch (error) {
            console.error("Erro no inativarUsuario():", error);
        }
    }

    function ordenar(usuarios, ordem = state.ordem) {
        if (ordem === "") {
            return usuarios;
        }
        if (ordem === "Alfabetica") {
            return [...usuarios].sort(function (a, b) {
                if (a.nome_pessoa.toLowerCase() > b.nome_pessoa.toLowerCase()) {
                    return 1;
                }
                if (a.nome_pessoa.toLowerCase() < b.nome_pessoa.toLowerCase()) {
                    return -1;
                }
                return 0;
            })
        }
        if (ordem === "Criacao") {
            return [...usuarios].sort(function (a, b) {
                return new Date(a.dta_cadastro) - new Date(b.dta_cadastro);
            })
        }
        return usuarios;
    }

    return (
        <div className="admin-page-container">
            <div className="admin-header-container">
                <div className="title-group">
                    <h2 className="admin-title">Gerenciamento de Usuários</h2>
                    <p className="admin-subtitle">Controle de acessos e permissões do sistema</p>
                </div>
                <Link to="/admin/usuarios/criar" className="btn-add-new">
                    <FontAwesomeIcon icon={faPlus} className="me-2" /> Novo Usuário
                </Link>
            </div>

            <Card className="filter-card-admin mb-4">
                <Card.Body className="p-4">
                    {/* onSubmit faz o Enter em qualquer campo aplicar o filtro. Sem isso os
                        botões abaixo (sem type="button") ficavam como type="submit" padrão
                        do HTML, e o Enter disparava um submit nativo do form sem handler —
                        ou seja, recarregava a página em vez de filtrar. */}
                    <Form onSubmit={(e) => { e.preventDefault(); filtrarUsuarios(); }}>
                        <div className="row g-3">
                            <Form.Group className="col-md-4">
                                <Form.Label className="label-admin">Nome</Form.Label>
                                <Form.Control 
                                    className="input-moderno" 
                                    placeholder="Buscar por nome..." 
                                    name="nome_pessoa" 
                                    value={state.filtros.nome_pessoa} 
                                    onChange={handleChangeFiltros} 
                                />
                            </Form.Group>
                            <Form.Group className="col-md-4">
                                <Form.Label className="label-admin">Função</Form.Label>
                                <Form.Select className="input-moderno" name="funcao" value={state.filtros.funcao} onChange={handleChangeFiltros}>
                                    <option value="">Todas as funções</option>
                                    <option value="Desenvolvedor">Desenvolvedor</option>
                                    <option value="Administrador">Administrador</option>
                                    <option value="Funcionario">Funcionário</option>
                                </Form.Select>
                            </Form.Group>
                            <Form.Group className="col-md-4">
                                <Form.Label className="label-admin">Status</Form.Label>
                                <Form.Select className="input-moderno" name="status_usuario" value={state.filtros.status_usuario} onChange={handleChangeFiltros}>
                                    <option value="">Todos</option>
                                    <option value="Ativo">Ativo</option>
                                    <option value="Inativo">Inativo</option>
                                </Form.Select>
                            </Form.Group>
                        </div>

                        <div className="filter-actions-row">
                            <div className="d-flex gap-2">
                                <Button type="button" className="btn-apply-filters" onClick={() => filtrarUsuarios()}>
                                    <FontAwesomeIcon icon={faFilter} className="me-2" /> Filtrar
                                </Button>
                                <Button type="button" className="btn-clear-filters" onClick={limparFiltros} title="Limpar">
                                    <FontAwesomeIcon icon={faSync} />
                                </Button>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <span className="text-muted small fw-bold">ORDENAR POR:</span>
                                <Form.Select
                                    className="small-select input-moderno w-auto"
                                    value={state.ordem}
                                    onChange={(e) => {
                                        // Passa a ordenação nova direto pra filtrarUsuarios, em vez
                                        // de confiar no state.ordem (que só reflete a troca no
                                        // próximo render) — era por isso que trocar "Ordenar por"
                                        // não reordenava nada.
                                        const novoOrdem = e.target.value;
                                        setState((prev) => ({ ...prev, ordem: novoOrdem }));
                                        filtrarUsuarios(state.filtros, novoOrdem);
                                    }}
                                >
                                    <option value="">Padrão</option>
                                    <option value="Alfabetica">Nome (A-Z)</option>
                                </Form.Select>
                            </div>
                        </div>
                    </Form>
                </Card.Body>
            </Card>

            <Card className="table-card-admin">
                <div className="table-responsive">
                    <Table className="modern-table mb-0">
                        <thead>
                            <tr>
                                <th style={{ width: '60px' }}>ID</th>
                                <th>Nome</th>
                                <th>Função</th>
                                <th>Status</th>
                                <th className="text-center">Opções</th>
                            </tr>
                        </thead>
                        <tbody>
                            {state.loading ? (
                                <tr><td colSpan="5" className="text-center py-5"><div className="spinner-border text-teal"></div></td></tr>
                            ) : state.usuarios.length > 0 ? (
                                state.usuarios.map((item, index) => (
                                    <tr key={index}>
                                        <td data-label="ID" className="text-muted small">#{item.id_usuario}</td>
                                        <td data-label="Nome" className="fw-bold">{item.nome_pessoa}</td>
                                        <td data-label="Função">
                                            <span className="text-muted"><FontAwesomeIcon icon={faUserShield} className="me-1 small" /> {item.funcao}</span>
                                        </td>
                                        <td data-label="Status">
                                            <span className={`status-badge ${item.status_usuario === "Ativo" ? "active" : "inactive"}`}>
                                                {item.status_usuario}
                                            </span>
                                        </td>
                                        <td data-label="Opções">
                                            <div className="d-flex justify-content-center gap-2">
                                                <Link className="action-icon view" to={`/admin/usuarios/${item.id_usuario}`} state={{ acao: "" }}><FontAwesomeIcon icon={faEye} /></Link>
                                                <Link className="action-icon edit" to={`/admin/usuarios/${item.id_usuario}`} state={{ acao: "Editar" }}><FontAwesomeIcon icon={faPen} /></Link>
                                                <button 
                                                    className="action-icon toggle" 
                                                    onClick={() => setState({ ...state, mostrarModalInativacao: true, registroAExcluir: item })}
                                                >
                                                    <FontAwesomeIcon icon={item.status_usuario === "Ativo" ? faToggleOn : faToggleOff} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan="5" className="text-center py-4">Nenhum usuário encontrado.</td></tr>
                            )}
                        </tbody>
                    </Table>
                </div>
                <div className="card-footer bg-white border-0 py-3 d-flex justify-content-center">
                    <PaginatedItems itemCount={state.count} itemsPerPage={20} handlePageChange={handlePageChange} />
                </div>
            </Card>

            <Modal show={state.mostrarModalInativacao} onHide={() => setState({ ...state, mostrarModalInativacao: false })} centered className="modern-modal">
                <Modal.Header closeButton className="modal-header-teal">
                    <Modal.Title className="text-white fs-5">Alterar Status</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <p className="mb-0">Deseja alterar o status do usuário <strong>{state.registroAExcluir.nome_pessoa}</strong> para <strong>{state.registroAExcluir.status_usuario === "Ativo" ? "Inativo" : "Ativo"}</strong>?</p>
                </Modal.Body>
                <Modal.Footer className="border-0 p-4 pt-0">
                    <Button variant="light" className="btn-modal-cancel" onClick={() => setState({ ...state, mostrarModalInativacao: false })}>Cancelar</Button>
                    <Button variant="danger" className="px-4" onClick={inativarUsuario}>Confirmar</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default GerenciarUsuarios;
